import { apiErrorSchema, enquiryReceiptSchema, paginatedListingsSchema, publicAreaSchema, publicDevelopmentSchema, publicEstatePlanSchema, publicEstateSchema, publicListingSchema, publicMapOverlaySchema, publicRegionSchema, type EnquiryInput, type ListingFilters } from "@nce/types";
import { z } from "zod";

export interface FeatureCollection {
  type: "FeatureCollection";
  features: Array<{ type: "Feature"; id?: string | number; geometry: unknown; properties: Record<string, unknown> }>;
}

function featureCollection(value: unknown): FeatureCollection {
  const candidate = Array.isArray(value) && value.length === 1 ? value[0] : value;
  if (!candidate || typeof candidate !== "object") throw new Error("API returned an invalid GeoJSON feature collection");
  const collection = candidate as Partial<FeatureCollection>;
  if (collection.type !== "FeatureCollection" || !Array.isArray(collection.features)) throw new Error("API returned an invalid GeoJSON feature collection");
  return collection as FeatureCollection;
}

export class NceApiError extends Error {
  constructor(public readonly code: string, message: string, public readonly correlationId: string, public readonly status: number, public readonly details?: unknown) { super(message); }
}

export class NceApiClient {
  private readonly baseUrl: string;
  constructor(baseUrl: string, private readonly fetcher: typeof fetch = fetch) { this.baseUrl = baseUrl.replace(/\/$/, ""); }

  private async request<T>(path: string, schema: z.ZodType<T>, init?: RequestInit): Promise<T> {
    const response = await this.fetcher(`${this.baseUrl}${path}`, { ...init, headers: { accept: "application/json", ...init?.headers }, signal: init?.signal ?? AbortSignal.timeout(20_000) });
    const json: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const parsed = apiErrorSchema.safeParse(json);
      throw parsed.success ? new NceApiError(parsed.data.error.code, parsed.data.error.message, parsed.data.error.correlationId, response.status, parsed.data.error.details) : new NceApiError("invalid_response", `API request failed with HTTP ${response.status}`, response.headers.get("x-correlation-id") ?? "unknown", response.status, json);
    }
    return schema.parse(json);
  }

  private query(filters: Partial<ListingFilters>): string {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
      if (value === undefined || value === null || value === "") continue;
      if (Array.isArray(value)) for (const item of value) params.append(key, String(item)); else params.set(key, String(value));
    }
    const query = params.toString(); return query ? `?${query}` : "";
  }

  async regions() { return (await this.request("/regions", z.object({ data: z.array(publicRegionSchema) }))).data; }
  async areas() { return (await this.request("/areas", z.object({ data: z.array(publicAreaSchema) }))).data; }
  async estates() { return (await this.request("/estates", z.object({ data: z.array(publicEstateSchema) }))).data; }
  async estate(slug: string) { return (await this.request(`/estates/${encodeURIComponent(slug)}`, z.object({ data: publicEstateSchema }))).data; }
  async estatePlan(slug: string) { return (await this.request(`/estates/${encodeURIComponent(slug)}/plan`, z.object({ data: publicEstatePlanSchema }))).data; }
  async estateListings(slug: string, filters: Partial<ListingFilters> = {}) { return this.request(`/estates/${encodeURIComponent(slug)}/listings${this.query(filters)}`, paginatedListingsSchema); }
  async estateMap(slug: string) { return featureCollection(await this.request(`/estates/${encodeURIComponent(slug)}/map`, z.unknown())); }
  async developments() { return (await this.request("/developments", z.object({ data: z.array(publicDevelopmentSchema) }))).data; }
  async development(slug: string) { return (await this.request(`/developments/${encodeURIComponent(slug)}`, z.object({ data: publicDevelopmentSchema }))).data; }
  async listings(filters: Partial<ListingFilters> = {}) { return this.request(`/listings${this.query(filters)}`, paginatedListingsSchema); }
  async listing(referenceOrSlug: string) { return (await this.request(`/listings/${encodeURIComponent(referenceOrSlug)}`, z.object({ data: publicListingSchema }))).data; }
  async mapEstates() { return featureCollection(await this.request("/map/estates", z.unknown())); }
  async mapListings(filters: Partial<ListingFilters> = {}) { return featureCollection(await this.request(`/map/listings${this.query(filters)}`, z.unknown())); }
  async mapPois(estate?: string) { return featureCollection(await this.request(`/map/pois${estate ? `?estate=${encodeURIComponent(estate)}` : ""}`, z.unknown())); }
  async mapOverlays(estate?: string) {
    const query = new URLSearchParams({ v: "actual-estate-plans-v2" });
    if (estate) query.set("estate", estate);
    return (await this.request(`/map/overlays?${query.toString()}`, z.object({ data: z.array(publicMapOverlaySchema) }))).data;
  }
  async enquire(input: EnquiryInput, idempotencyKey = crypto.randomUUID()) {
    return (await this.request("/enquiries", z.object({ data: enquiryReceiptSchema }), { method: "POST", headers: { "content-type": "application/json", "idempotency-key": idempotencyKey }, body: JSON.stringify(input) })).data;
  }
}
